import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { create } from 'zustand';
import { captureSessionMessage } from 'src/utils/sentry/sessionSentry';

const DEFAULT_UI_USER_INACTIVE_WARNING_IN_MINUTES = 15;

/**
 * Parse session expiry minutes from env/config.
 * Uses VITE_UI_USER_INACTIVE_WARNING_IN_MINUTES, default 15.
 */
export function getSessionExpiryMinutes(): number {
  try {
    const config = getRuntimeConfig();
    const raw = config.VITE_UI_USER_INACTIVE_WARNING_IN_MINUTES;
    if (typeof raw === 'number' && raw > 0) return raw;
    if (typeof raw === 'string') {
      const n = Number.parseInt(raw, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
  } catch {
    // ignore
  }
  return DEFAULT_UI_USER_INACTIVE_WARNING_IN_MINUTES;
}

/**
 * Returns true when user has been inactive long enough that we should
 * pause token refresh (use cached token only, show session warning).
 * Callable from non-React code (e.g. authTokenHttp).
 */
export function getShouldPauseTokenRefresh(): boolean {
  const state = useUserInactivityStore.getState();
  if (state.forcePauseTokenRefresh) {
    return true;
  }
  const expiryMs = getSessionExpiryMinutes() * 60 * 1000;
  return Date.now() - state.lastActivityAt >= expiryMs;
}

interface UserInactivityState {
  /** Last time user activity was recorded (e.g. click). */
  lastActivityAt: number;
  /** Explicit override to pause token refresh (e.g. when session warning is visible). */
  forcePauseTokenRefresh: boolean;
  actions: {
    /** Record user activity (call on click). */
    recordActivity: () => void;
    /** Reset inactivity state (e.g. after user continues session). */
    resetInactivity: () => void;
    /** Forcefully pause or resume token refresh. */
    setForcePauseTokenRefresh: (pause: boolean) => void;
  };
}

export const useUserInactivityStore = create<UserInactivityState>((set) => ({
  lastActivityAt: Date.now(),
  forcePauseTokenRefresh: false,

  actions: {
    recordActivity: () => set({ lastActivityAt: Date.now() }),

    resetInactivity: () =>
      set({ lastActivityAt: Date.now(), forcePauseTokenRefresh: false }),

    setForcePauseTokenRefresh: (pause: boolean) => {
      captureSessionMessage('Token refresh pause state changed', 'info', {
        event: pause
          ? 'session.inactivity.pauseTokenRefresh'
          : 'session.inactivity.resumeTokenRefresh',
      });
      set({ forcePauseTokenRefresh: pause });
    },
  },
}));
